"use client";

import LandingTerminalLink from "./LandingTerminalLink";

// Brand porcelain accent (lib/bastion-accent.ts → #DCE2EC)
const PORCELAIN = "220, 226, 236";

// Fine grain used to dither the dark→light gradient. Dark gradients band badly
// at 8-bit depth; a faint noise overlay breaks the steps into a smooth ramp.
const NOISE =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='180' height='180'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";

function BulletMark() {
  return (
    <span
      aria-hidden="true"
      className="shrink-0 w-[5px] h-[5px]"
      style={{ backgroundColor: `rgba(${PORCELAIN}, 0.55)` }}
    />
  );
}

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <span
      className="inline-block text-[11px] font-semibold uppercase tracking-[0.16em] mb-3"
      style={{ color: `rgba(${PORCELAIN}, 0.7)` }}
    >
      {children}
    </span>
  );
}

/* ── Track record: three real Bastion portfolios, one per strategy and
   region. Monthly series are indexed to 100 at inception (Jun 2024) and pulled
   live from portfolio_timeseries; the S&P 500 line is the shared benchmark, so
   the outperformance shown is honest, not stylised. ── */

const SP500_SERIES = [
  100, 101.21, 103.58, 105.75, 104.81, 111.06, 108.39, 111.3, 109.88, 103.76,
  102.86, 109.33, 114.94, 117.59, 120, 123.93, 126.89, 127.14, 126.86, 128.72,
  127.65, 120.99, 133.69, 140.74, 139.3, 139.37, 143.12, 142.32,
];

const PORTFOLIOS = [
  {
    name: "US Growth Rockets",
    strategy: "Growth + Momentum",
    region: "United States",
    regionKind: "us" as const,
    href: "/portfolios/growthmomentum-sp500",
    series: [
      100, 97.53, 98.87, 102.05, 100.02, 106.89, 101.75, 103.71, 97.92, 86.86,
      88.49, 95.88, 102.26, 103.87, 108.53, 117.03, 124.11, 117.31, 117.28,
      127.12, 139.75, 129.15, 166.49, 190.18, 210.87, 169.25, 172.26, 174.7,
    ],
  },
  {
    name: "Europe Smart Value",
    strategy: "Value + Momentum",
    region: "Europe",
    regionKind: "eu" as const,
    href: "/portfolios/valuemomentum-europe-500",
    series: [
      100, 107.79, 106.43, 107.73, 103.87, 104.72, 105.93, 115.1, 126.38,
      128.76, 134.04, 149.25, 153.01, 159.62, 166.28, 173.75, 177.65, 179.34,
      192.1, 204.17, 201.53, 195.11, 208.24, 206.6, 209.99, 224.53, 230.35,
      236.26,
    ],
  },
  {
    name: "Global GEAR+",
    strategy: "Multi-factor",
    region: "Global",
    regionKind: "global" as const,
    href: "/portfolios/gear5f-exusa-500",
    series: [
      100, 100.17, 103.54, 120.7, 115.41, 113.14, 113.02, 117.05, 121.31,
      122.56, 122.31, 126.28, 129.42, 134.21, 147.09, 157.23, 155.36, 156.38,
      156.6, 163.58, 171.62, 154.29, 170.36, 168.23, 166.05, 163.92, 173.36,
      168.62,
    ],
  },
] as const;

/** Twelve EU stars on a circle. Fixed decimals so Node and the browser emit the
    same SVG attributes. sin/cos differ in the last digit and break hydration. */
const EU_STARS: ReadonlyArray<readonly [number, number]> = [
  [10, 3],
  [12, 3.536],
  [13.464, 5],
  [14, 7],
  [13.464, 9],
  [12, 10.464],
  [10, 11],
  [8, 10.464],
  [6.536, 9],
  [6, 7],
  [6.536, 5],
  [8, 3.536],
];

/** Cross-platform region marks. Regional-indicator flag emoji (🇺🇸/🇪🇺) don't
    render as flags on Windows (they fall back to letters), so we draw small
    inline SVGs that look identical on every OS. */
function RegionMark({ kind }: { kind: "us" | "eu" | "global" }) {
  const flagBox =
    "inline-block h-[11px] w-[15px] rounded-[2px] overflow-hidden shrink-0 ring-1 ring-white/15";
  const stripeH = 14 / 13;

  if (kind === "us") {
    return (
      <svg viewBox="0 0 20 14" className={flagBox} aria-hidden="true">
        <rect width="20" height="14" fill="#f4f5f7" />
        {[0, 2, 4, 6, 8, 10, 12].map((i) => (
          <rect key={i} y={i * stripeH} width="20" height={stripeH} fill="#be2b3f" />
        ))}
        <rect width="9" height={stripeH * 7} fill="#2a3b8f" />
      </svg>
    );
  }

  if (kind === "eu") {
    return (
      <svg viewBox="0 0 20 14" className={flagBox} aria-hidden="true">
        <rect width="20" height="14" fill="#123a8f" />
        {EU_STARS.map(([cx, cy], i) => (
          <circle key={i} cx={cx} cy={cy} r="0.85" fill="#f5d020" />
        ))}
      </svg>
    );
  }

  return (
    <svg
      viewBox="0 0 16 16"
      className="inline-block h-[13px] w-[13px] shrink-0"
      aria-hidden="true"
    >
      <circle cx="8" cy="8" r="6.4" fill="none" stroke="rgba(220,226,236,0.6)" strokeWidth="1.1" />
      <ellipse cx="8" cy="8" rx="2.7" ry="6.4" fill="none" stroke="rgba(220,226,236,0.6)" strokeWidth="1.1" />
      <line x1="1.6" y1="8" x2="14.4" y2="8" stroke="rgba(220,226,236,0.6)" strokeWidth="1.1" />
    </svg>
  );
}

const SIGNAL_GREEN = "74, 222, 128";

function pct(series: readonly number[]): string {
  const change = series[series.length - 1] - series[0];
  return `${change >= 0 ? "+" : ""}${Math.round(change)}%`;
}

/** Catmull-Rom → cubic Bézier: turns the monthly points into one smooth curve
    instead of a jagged polyline. */
function smoothPath(pts: { x: number; y: number }[]): string {
  if (pts.length < 2) return "";
  let d = `M${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] ?? pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] ?? p2;
    const c1x = p1.x + (p2.x - p0.x) / 6;
    const c1y = p1.y + (p2.y - p0.y) / 6;
    const c2x = p2.x - (p3.x - p1.x) / 6;
    const c2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C${c1x.toFixed(1)} ${c1y.toFixed(1)} ${c2x.toFixed(1)} ${c2y.toFixed(1)} ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`;
  }
  return d;
}

/** Smooth line + area for the portfolio, with a dashed S&P 500 reference on the
    same scale. Pure geometry, no chart lib — crisp at any size. */
function PerfChart({ series, uid }: { series: readonly number[]; uid: string }) {
  const W = 320;
  const H = 132;
  const padX = 4;
  const padTop = 12;
  const padBot = 6;

  const all = [...series, ...SP500_SERIES];
  const min = Math.min(...all);
  const max = Math.max(...all);
  const span = max - min || 1;

  const pointsOf = (s: readonly number[]) =>
    s.map((v, i) => ({
      x: padX + (i / (s.length - 1)) * (W - 2 * padX),
      y: padTop + (1 - (v - min) / span) * (H - padTop - padBot),
    }));

  const linePath = smoothPath(pointsOf(series));
  const areaPath = `${linePath} L${(W - padX).toFixed(1)} ${H - padBot} L${padX} ${H - padBot} Z`;
  const gid = `pf-fill-${uid}`;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      className="w-full h-32 md:h-36"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={`rgba(${SIGNAL_GREEN},0.3)`} />
          <stop offset="100%" stopColor={`rgba(${SIGNAL_GREEN},0)`} />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#${gid})`} />
      <path
        d={smoothPath(pointsOf(SP500_SERIES))}
        fill="none"
        stroke="rgba(255,255,255,0.3)"
        strokeWidth="1.4"
        strokeDasharray="4 4"
        vectorEffect="non-scaling-stroke"
      />
      <path
        d={linePath}
        fill="none"
        stroke={`rgb(${SIGNAL_GREEN})`}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

function PortfolioCard({ p }: { p: (typeof PORTFOLIOS)[number] }) {
  return (
    <LandingTerminalLink
      href={p.href}
      className="group rounded-xl overflow-hidden border border-white/[0.08] bg-[#171b24] flex flex-col transition-colors hover:border-white/20 hover:bg-[#1b202a]"
    >
      <div className="flex items-center justify-between px-4 pt-4">
        <span
          className="text-[10px] font-semibold uppercase tracking-[0.14em]"
          style={{ color: `rgba(${SIGNAL_GREEN},0.9)` }}
        >
          {p.strategy}
        </span>
        <span className="flex items-center gap-1.5 text-xs text-white/55">
          <RegionMark kind={p.regionKind} />
          {p.region}
        </span>
      </div>

      <div className="mx-3 mt-3 rounded-lg bg-black/25 ring-1 ring-white/5 px-1 pt-2">
        <PerfChart series={p.series} uid={p.regionKind} />
      </div>

      <div className="px-4 pb-4 pt-3">
        <div className="text-sm font-semibold text-white/90">{p.name}</div>
        <div className="mt-1 flex items-baseline gap-2">
          <span
            className="text-xl font-semibold tabular-nums"
            style={{ color: `rgb(${SIGNAL_GREEN})` }}
          >
            {pct(p.series)}
          </span>
          <span className="text-xs text-white/50">
            vs S&amp;P 500 {pct(SP500_SERIES)}
          </span>
        </div>
      </div>
    </LandingTerminalLink>
  );
}

export default function WorkflowShowcase() {
  return (
    <section id="features" className="relative overflow-hidden pt-14 pb-16 md:pt-20 md:pb-24">
      {/* ── Porcelain "stage": full-width lift out of black, no centred hotspot ── */}
      {/* Fades out horizontally so the viewport edge always stays #0E0E0E:
          browsers that tint their frame with the page background would
          otherwise show a seam against the lighter band. */}
      <div
        className="absolute inset-0 pointer-events-none z-0"
        style={{
          WebkitMaskImage:
            "linear-gradient(90deg, transparent 0%, #000 18%, #000 82%, transparent 100%)",
          maskImage:
            "linear-gradient(90deg, transparent 0%, #000 18%, #000 82%, transparent 100%)",
        }}
      >
        {/* Broad elevation band: all three workflow cards stay lit, then the
            return to black is stretched across a long tail so the last card
            does not drop off a hard edge. */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, transparent 0%, rgba(31,35,45,0.5) 12%, rgba(31,35,45,0.92) 28%, rgba(31,35,45,0.92) 58%, rgba(31,35,45,0.62) 72%, rgba(31,35,45,0.32) 84%, rgba(31,35,45,0.12) 92%, transparent 100%)",
          }}
        />
        {/* Cool porcelain sheen follows the same long fade-out. */}
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(180deg, transparent 2%, rgba(${PORCELAIN},0.025) 12%, rgba(${PORCELAIN},0.055) 28%, rgba(${PORCELAIN},0.055) 58%, rgba(${PORCELAIN},0.03) 74%, rgba(${PORCELAIN},0.012) 88%, transparent 100%)`,
          }}
        />
        {/* Dither grain to kill 8-bit banding — masked to the same soft band so
            it never shows a hard edge, and kept very faint. */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: NOISE,
            backgroundSize: "200px 200px",
            opacity: 0.12,
            mixBlendMode: "overlay",
            WebkitMaskImage:
              "linear-gradient(180deg, transparent 0%, #000 18%, #000 62%, transparent 100%)",
            maskImage:
              "linear-gradient(180deg, transparent 0%, #000 18%, #000 62%, transparent 100%)",
          }}
        />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 md:px-6">
        {/* ── Header ── */}
        <div className="text-center max-w-2xl mx-auto mb-12 md:mb-16">
          <div className="mb-5">
            <Eyebrow>The Terminal</Eyebrow>
          </div>
          <h2 className="text-3xl sm:text-4xl font-semibold leading-tight mb-4 text-white">
            Go beyond one stock
          </h2>
          <p className="text-lg text-white/55 max-w-xl mx-auto">
            The same scores rank 4,000+ stocks, power 13 live portfolios, and
            map the whole market.
          </p>
        </div>

        {/* ══ Workflow 1 — Discover (Radar) : text left, wide strip right ══ */}
        <div className="group rounded-xl overflow-hidden border border-white/[0.08] bg-[#0b0d12] shadow-2xl grid grid-cols-1 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]">
          <div className="p-8 md:p-10 lg:p-12 flex flex-col justify-center order-2 lg:order-1">
            <Eyebrow>Discover</Eyebrow>
            <h3 className="text-2xl md:text-3xl font-semibold text-white leading-tight mb-4">
              From 4,000 stocks to a shortlist
            </h3>
            <p className="text-white/55 leading-relaxed mb-6">
              Pick a market and a strategy. The highest-rated stock is the best
              fit for it.
            </p>
            <ul className="space-y-2.5 mb-8">
              {[
                "9 Bastion strategies for different styles: Growth, Quality, Value",
                "4 multi-factor scores, including Growth + Efficiency at a Reasonable Price",
                "Ratings recalculated daily",
              ].map((item) => (
                <li key={item} className="flex items-center gap-3">
                  <BulletMark />
                  <span className="text-sm text-white/70">{item}</span>
                </li>
              ))}
            </ul>
            <div>
              <LandingTerminalLink
                href="/radar"
                className="inline-flex items-center gap-2 rounded-lg px-6 py-3 text-sm font-medium text-black transition-opacity hover:opacity-90"
                style={{ backgroundColor: `rgb(${PORCELAIN})` }}
              >
                Open Radar
                <span aria-hidden="true">→</span>
              </LandingTerminalLink>
            </div>
          </div>

          <div className="relative min-h-[240px] lg:min-h-[460px] order-1 lg:order-2 border-b lg:border-b-0 lg:border-l border-white/[0.06]">
            <img
              src="/landing/shot-radar.png"
              alt="Bastion Radar screener ranking stocks by strategy score"
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover object-left-top"
            />
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "linear-gradient(90deg, rgba(11,13,18,0.5) 0%, transparent 14%), linear-gradient(0deg, rgba(11,13,18,0.35) 0%, transparent 18%)",
              }}
            />
          </div>
        </div>

        {/* ══ Workflow 2 — Track record (Portfolios) : header + 3 strategy charts ══ */}
        <div className="rounded-xl overflow-hidden border border-white/[0.08] bg-[#0b0d12] shadow-2xl mt-6 p-8 md:p-10 lg:p-12">
          <div className="max-w-2xl mb-8">
            <Eyebrow>Track record</Eyebrow>
            <h3 className="text-2xl md:text-3xl font-semibold text-white leading-tight mb-4">
              See how the strategies perform
            </h3>
            <p className="text-white/55 leading-relaxed">
              13 systematic portfolios, built from the same scores you see on
              every stock. Rebalanced monthly and tracked since June 2024.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            {PORTFOLIOS.map((p) => (
              <PortfolioCard key={p.name} p={p} />
            ))}
          </div>
          <p className="mt-4 text-xs text-white/55">
            Live model performance since inception (Jun 2024), indexed to 100.
            Past performance is not indicative of future results.
          </p>

          <div className="mt-8">
            <LandingTerminalLink
              href="/portfolios"
              className="inline-flex items-center gap-2 text-sm font-medium transition-opacity hover:opacity-80"
              style={{ color: `rgb(${PORCELAIN})` }}
            >
              Browse all portfolios
              <span aria-hidden="true">→</span>
            </LandingTerminalLink>
          </div>
        </div>

        {/* ══ Workflow 3 — Monitor (Heatmap) : wide strip left, text right ══ */}
        <div className="group rounded-xl overflow-hidden border border-white/[0.08] bg-[#0b0d12] shadow-2xl mt-6 grid grid-cols-1 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]">
          <div className="relative min-h-[240px] lg:min-h-[460px] border-b lg:border-b-0 lg:border-r border-white/[0.06]">
            <img
              src="/landing/shot-heatmap.png"
              alt="Bastion heatmap: a color-coded map of a full stock universe with dozens of metrics"
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover object-left-top"
            />
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "linear-gradient(270deg, rgba(11,13,18,0.5) 0%, transparent 14%), linear-gradient(0deg, rgba(11,13,18,0.35) 0%, transparent 18%)",
              }}
            />
          </div>

          <div className="p-8 md:p-10 lg:p-12 flex flex-col justify-center">
            <Eyebrow>Monitor</Eyebrow>
            <h3 className="text-2xl md:text-3xl font-semibold text-white leading-tight mb-4">
              See the whole market in one screen
            </h3>
            <p className="text-white/55 leading-relaxed mb-6">
              Color thousands of stocks by any Bastion score or metric. Strong
              and weak areas stand out in seconds, so the outliers are easy to
              spot.
            </p>
            <div>
              <LandingTerminalLink
                href="/heatmap"
                className="inline-flex items-center gap-2 rounded-lg px-6 py-3 text-sm font-medium text-black transition-opacity hover:opacity-90"
                style={{ backgroundColor: `rgb(${PORCELAIN})` }}
              >
                Open Heatmap
                <span aria-hidden="true">→</span>
              </LandingTerminalLink>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
